import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Input } from '@epoch-ui/components/Input';
import { formatNumberWithComma } from 'utils/format';
import { useToaster } from 'hooks/useToaster';
import { ToasterVariant } from 'context/toasterContext';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import { updateEventBudget, UpdateEventBudgetInput } from '../../../api/update-event-budget';

const DEBOUNCE_DELAY = 500;

const extractNumericValue = (formattedValue: string): number | null => {
  const cleanValue = formattedValue.replace(/,/g, '');

  if (cleanValue === '') {
    return null;
  }

  const numericValue = parseInt(cleanValue, 10);

  if (Number.isNaN(numericValue) || numericValue < 0) {
    return null;
  }

  return numericValue;
};

const EventBudgetInput: React.FC = () => {
  const { budget, id: eventId, scheduledDeletionAt } = useEditableEvent();
  const queryClient = useQueryClient();
  const { showToast } = useToaster();

  const isCancelled = !!scheduledDeletionAt;

  const [inputValue, setInputValue] = useState('');

  const updateBudgetMutation = useMutation({
    mutationFn: (variables: UpdateEventBudgetInput) => updateEventBudget(variables),
    onSuccess: (updatedBudget, variables) => {
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                budget: updatedBudget
              }
            }
          : oldData
      );
    },
    onError: (error) => {
      console.error('Error updating budget:', error);
      showToast('Failed to update budget. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const displayValue = useMemo(() => {
    if (budget === 0) return '0';
    if (budget === null || budget === undefined) return '';
    return formatNumberWithComma(budget);
  }, [budget]);

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  useEffect(() => {
    // Don't trigger on initial render or if input matches display value
    if (inputValue === displayValue)
      return (): void => {
        /* noop */
      };

    const timeoutId = setTimeout(() => {
      const numericValue = extractNumericValue(inputValue);

      if (numericValue !== budget && eventId) {
        updateBudgetMutation.mutate({
          eventId,
          budget: numericValue
        });
      }
    }, DEBOUNCE_DELAY);

    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [inputValue, budget, eventId, displayValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const rawValue = e.target.value;

    const numericValue = extractNumericValue(rawValue);

    if (numericValue === null) {
      setInputValue('');
      return;
    }

    const formattedValue = formatNumberWithComma(numericValue);
    setInputValue(formattedValue);
  };

  return (
    <Input
      type="text"
      value={inputValue}
      disabled={isCancelled}
      onChange={handleInputChange}
      className="tw-max-w-[200px] tw-pl-7"
    />
  );
};

export { EventBudgetInput };
